import { StatusBadgeColorVariant } from "@/shared/components/StatusBadge";
import {
  PathPickCancelReason,
  PathPickShipmentStatus,
} from "@/features/pathpick/features/order-history/types";
import { ORDER_HISTORY_KEY } from "@/features/pathpick/features/order-history/constants";
import { PathPickOrderStepCode } from "@/features/pathpick/features/order-history/features/detail/types";

// 주문 상세 화면 i18n 접두사 — 부모 네임스페이스에서 파생한다.
export const ORDER_DETAIL_KEY = `${ORDER_HISTORY_KEY}detail.`;

// 주문 취소 성공 토스트 전용 id — 공용 "GLOBAL"을 쓰면 안 된다.
// useAppToasts는 언마운트될 때 자기 id의 토스트를 닫는데(use-app-toasts.ts의 cleanup),
// 취소가 성공하면 상태가 CANCELED로 바뀌면서 "GLOBAL"을 쓰는 컴포넌트들(취소 버튼,
// KREAM 파일 카드)이 동시에 언마운트돼 방금 띄운 토스트까지 함께 지워진다.
export const ORDER_CANCEL_TOAST_ID = "PATHPICK_ORDER_CANCEL";

// 진행 단계 code → 라벨 키. 응답에 steps[]가 없어 단계는 status로 파생하고
// 라벨은 항상 이 매핑을 쓴다.
export const ORDER_STEP_LABEL_KEYS: Record<PathPickOrderStepCode, string> = {
  REMITTANCE_REQUESTED: `${ORDER_DETAIL_KEY}step_payment_request`,
  QUANTITY_CHECKING: `${ORDER_DETAIL_KEY}step_quantity_check`,
  QUANTITY_CONFIRMED: `${ORDER_DETAIL_KEY}step_quantity_confirmed`,
  SHIPPING: `${ORDER_DETAIL_KEY}step_shipping`,
  COMPLETED: `${ORDER_DETAIL_KEY}step_delivered`,
};

// 스텝퍼에 노출하는 단계 순서. 명세상 미사용인 REQUESTED/REMITTED는 각각
// 입금 요청/수량 확인 단계로 흡수하므로(toPathPickOrderSteps) 여기에는 넣지 않는다.
export const ORDER_DETAIL_STEP_CODES: PathPickOrderStepCode[] = [
  "REMITTANCE_REQUESTED",
  "QUANTITY_CHECKING",
  "QUANTITY_CONFIRMED",
  "SHIPPING",
  "COMPLETED",
];

// 상세 카드 공통 셸
export const ORDER_DETAIL_CARD_SX = {
  border: "0.5px solid #D4D4D1",
  borderRadius: "8px",
  background: "#FFFFFF",
} as const;

// 상품 옵션 테이블 — 사이즈 / 단가 / 요청 / 취소 / 취소 사유 / 확정 / 합계
export const ORDER_DETAIL_OPTION_TEMPLATE_COLUMNS =
  "80px minmax(120px, 1fr) 100px 100px 130px 100px minmax(120px, 1fr)";

// 헤더 컬럼 — 라벨 키와 정렬을 함께 들고 있어 인덱스로 정렬을 판정하지 않는다.
export const ORDER_DETAIL_OPTION_COLUMNS = [
  { labelKey: `${ORDER_DETAIL_KEY}col_size`, textAlign: "left" },
  { labelKey: `${ORDER_DETAIL_KEY}col_unit_price`, textAlign: "right" },
  { labelKey: `${ORDER_DETAIL_KEY}col_requested`, textAlign: "center" },
  { labelKey: `${ORDER_DETAIL_KEY}col_canceled`, textAlign: "center" },
  { labelKey: `${ORDER_DETAIL_KEY}col_cancel_reason`, textAlign: "left" },
  { labelKey: `${ORDER_DETAIL_KEY}col_confirmed`, textAlign: "left" },
  { labelKey: `${ORDER_DETAIL_KEY}col_total`, textAlign: "right" },
] as const satisfies ReadonlyArray<{
  labelKey: string;
  textAlign: "left" | "right" | "center";
}>;

// 옵션 취소 사유 code → 라벨 키. 미매핑 code는 cancelReasonNote/원문으로 폴백한다.
// 키를 enum으로 좁혀 서버가 사유를 추가하면 tsc가 잡게 한다.
export const CANCEL_REASON_LABEL_KEYS: Record<PathPickCancelReason, string> = {
  SOLD_OUT: `${ORDER_DETAIL_KEY}cancel_reason_sold_out`,
  AGENCY_CANCELED: `${ORDER_DETAIL_KEY}cancel_reason_agency_canceled`,
  BUYER_CANCELED: `${ORDER_DETAIL_KEY}cancel_reason_buyer_canceled`,
  OTHER: `${ORDER_DETAIL_KEY}cancel_reason_other`,
};

// 배송 건 상태 → 배지 라벨 키/색상.
// 색은 주문 대표 상태 축(준비/배송 중/완료)에 맞추고, 세부 단계는 라벨로 구분한다 —
// 가운데 네 단계는 서버에서도 SHIPPING 하나로 묶이는 구간이라 같은 색을 쓴다.
export const SHIPMENT_STATUS_META: Record<
  PathPickShipmentStatus,
  { labelKey: string; colorVariant: StatusBadgeColorVariant }
> = {
  READY: {
    labelKey: `${ORDER_DETAIL_KEY}shipping_state_ready`,
    colorVariant: "gray",
  },
  OVERSEAS_SHIPPING: {
    labelKey: `${ORDER_DETAIL_KEY}shipping_state_overseas_shipping`,
    colorVariant: "blue",
  },
  CUSTOMS_CLEARANCE: {
    labelKey: `${ORDER_DETAIL_KEY}shipping_state_customs_clearance`,
    colorVariant: "blue",
  },
  DOMESTIC_SHIPPING_PENDING: {
    labelKey: `${ORDER_DETAIL_KEY}shipping_state_domestic_shipping_pending`,
    colorVariant: "blue",
  },
  DOMESTIC_SHIPPING: {
    labelKey: `${ORDER_DETAIL_KEY}shipping_state_domestic_shipping`,
    colorVariant: "blue",
  },
  DELIVERED: {
    labelKey: `${ORDER_DETAIL_KEY}shipping_state_delivered`,
    colorVariant: "green",
  },
};

// 출고 이후 배송 완료 전 구간 — 주문 대표 상태에서 SHIPPING 하나로 묶이는 네 단계.
export const SHIPMENT_IN_TRANSIT_STATUSES: PathPickShipmentStatus[] = [
  "OVERSEAS_SHIPPING",
  "CUSTOMS_CLEARANCE",
  "DOMESTIC_SHIPPING_PENDING",
  "DOMESTIC_SHIPPING",
];
